import React, { useState } from "react";
import { Award, Briefcase, Car, Check, Clapperboard, GraduationCap, HeartPulse, Infinity as InfinityIcon, Languages, MonitorCog, RefreshCw, Rocket, Scale, ShieldCheck, Sparkles, Trophy, UserSearch } from "lucide-react";
import { ProductGrid } from "./ProductGrid";
import { BundleCard } from "./GamesSection";
import { Reveal } from "./Reveal";
import { APP_CATEGORIES, AVAILABLE_APPS } from "../data/apps";

const CAT_ICONS = { edukacja: GraduationCap, motoryzacja: Car, zdrowie: HeartPulse, firma: Briefcase, prawo: Scale, technologia: MonitorCog, multimedia: Clapperboard, sport: Trophy, kariera: UserSearch };

const OWNERSHIP_POINTS = [
  { icon: ShieldCheck, text: "Kupujesz raz — aplikacja jest Twoja na zawsze" },
  { icon: Award, text: "Pełne wsparcie techniczne zawsze w cenie" },
  { icon: RefreshCw, text: "Regularne aktualizacje — w cenie, bez dopłat" },
  { icon: Rocket, text: "Każda aktualizacja rozwija możliwości Twojej aplikacji" },
];

const COMPARE_ROWS = [
  ["Płatność", "Co miesiąc, w nieskończoność", "Raz — i jest Twoja na zawsze"],
  ["Po rezygnacji", "Tracisz dostęp do wszystkiego", "Aplikacja zostaje Twoja na zawsze"],
  ["Koszt po 2 latach", "od ~700 zł i więcej", "od 39,99 zł — koniec wydatków"],
  ["Aktualizacje", "Kończą się wraz z płatnościami", "Regularnie, w cenie, bez dopłat"],
  ["Wsparcie techniczne", "Zależne od planu", "Pełne, zawsze w cenie"],
];

export function AppsSection({ apps }) {
  const [sub, setSub] = useState("all");
  React.useEffect(() => {
    const h = (e) => setSub(e.detail);
    window.addEventListener("set-app-subcategory", h);
    return () => window.removeEventListener("set-app-subcategory", h);
  }, []);
  // ta sama przynaleznosc kategorii co na pelnej stronie /aplikacje (AVAILABLE_APPS.categories), nie
  // product.category/subcategory z bazy - te pola historycznie sa niespojnie nazwane miedzy produktami
  const clean = apps.filter((a) => {
    const m = AVAILABLE_APPS.find((x) => x.storeId === a.id);
    return m && !m.categories.includes("pakiety");
  });
  const catOf = (a) => AVAILABLE_APPS.find((x) => x.storeId === a.id)?.categories || [];
  const bundleApps = apps.find((a) => a.id === "pakiet-aplikacje");
  const shown = sub === "all" ? clean : clean.filter((a) => catOf(a).includes(sub));
  if (!apps.length) return null;
  return (
    <section id="aplikacje" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="apps-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-fuchsia-500/20">
          <div className="relative h-64 sm:h-80">
            <img
              src="/img/hero/apps-banner-v2.jpg"
              alt="Aplikacje DigiLib — prawo, finanse, auto, biuro, nauka i gry"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/60 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12">
              <span className="w-fit rounded-full bg-gradient-to-r from-fuchsia-400 to-cyan-400 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-950">
                Nowa kategoria
              </span>
              <h2 className="mt-4 font-display text-2xl font-extrabold text-white sm:text-4xl">
                Interaktywne <span className="gradient-text">aplikacje</span>
              </h2>
              <p className="mt-2 max-w-lg text-sm text-slate-300 sm:text-base">
                Nauka, firma, prawo, motoryzacja i więcej — osobny świat aplikacji obok ebooków i audiobooków.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  { icon: Languages, label: "Nauka i korepetycje" },
                  { icon: Briefcase, label: "Firma i prawo" },
                  { icon: GraduationCap, label: "Wirtualny korepetytor AI" },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <span key={c.label} className="flex items-center gap-1.5 rounded-full bg-slate-950/60 border border-cyan-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-cyan-200 backdrop-blur">
                      <Icon className="h-3.5 w-3.5" /> {c.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={120}>
        <div className="glow-border mt-10 rounded-3xl p-[2px]" data-testid="ownership-ad">
          <div className="relative overflow-hidden rounded-3xl bg-slate-950/95 px-6 py-10 sm:px-12">
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-teal-500/15 blur-3xl" />
            <div className="relative grid items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-teal-500/40 bg-teal-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-teal-300">
                  <InfinityIcon className="h-3.5 w-3.5" /> Na zawsze, z darmowymi aktualizacjami
                </span>
                <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
                  Kupujesz raz. <span className="gradient-text">Masz na zawsze.</span>
                </h3>
                <div className="mt-6 space-y-3">
                  {OWNERSHIP_POINTS.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-cyan-500">
                          <Icon className="h-3.5 w-3.5 text-slate-950" />
                        </span>
                        <p className="text-sm leading-relaxed text-slate-300">{p.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="overflow-hidden rounded-2xl border border-cyan-500/15">
                <table className="w-full text-left text-xs sm:text-sm" data-testid="ownership-compare-table">
                  <thead>
                    <tr className="bg-slate-900/60 text-slate-400">
                      <th className="px-4 py-3 font-semibold"></th>
                      <th className="px-4 py-3 font-semibold">U innych — opłaty co miesiąc</th>
                      <th className="px-4 py-3 font-bold text-cyan-300">Aplikacje DigiLib</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((r, i) => (
                      <tr key={i} className="border-t border-slate-800/60">
                        <td className="px-4 py-3 font-semibold text-slate-300">{r[0]}</td>
                        <td className="px-4 py-3 text-slate-500">{r[1]}</td>
                        <td className="px-4 py-3">
                          <span className="flex items-start gap-1.5 font-medium text-teal-300">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {r[2]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-10 flex flex-wrap gap-2.5" data-testid="apps-subcategory-tabs">
        <button
          data-testid="apps-sub-all"
          onClick={() => setSub("all")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${sub === "all" ? "text-slate-950 shadow-lg" : "glass-panel text-slate-300 hover:border-fuchsia-400/40"}`}
          style={sub === "all" ? { background: "linear-gradient(90deg, #e879f9, #22d3ee)" } : {}}
        >
          <Sparkles className="h-4 w-4" /> Wszystkie
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${sub === "all" ? "bg-slate-950/20" : "bg-slate-800"}`}>{clean.length}</span>
        </button>
        {APP_CATEGORIES.filter((c) => c.id !== "pakiety").map((c) => {
          const Icon = CAT_ICONS[c.id] || Sparkles;
          const isActive = sub === c.id;
          const count = clean.filter((a) => catOf(a).includes(c.id)).length;
          if (!count) return null;
          return (
            <button
              key={c.id}
              data-testid={`apps-sub-${c.id}`}
              onClick={() => setSub(c.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${isActive ? "text-slate-950 shadow-lg" : "glass-panel text-slate-300 hover:border-fuchsia-400/40"}`}
              style={isActive ? { background: "linear-gradient(90deg, #e879f9, #22d3ee)" } : {}}
            >
              <Icon className="h-4 w-4" /> {c.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${isActive ? "bg-slate-950/20" : "bg-slate-800"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div key={sub} className="mt-6">
        <ProductGrid products={shown} testid="apps-grid" />
      </div>
      <Reveal delay={200}>
        <p className="mt-8 flex items-center justify-center gap-2 text-center text-sm text-slate-500">
          <Sparkles className="h-4 w-4 text-fuchsia-400" />
          Kolejne języki i przedmioty już w produkcji — aplikacje, które kupisz dziś, dostaną je w aktualizacjach.
        </p>
      </Reveal>

      {/* PAKIET MEGA — wszystkie aplikacje i gry; największa oszczędność w sklepie */}
      {bundleApps && (
        <div className="mt-10">
          <BundleCard
            testid="bundle-apps-card"
            img="/img/tiles/aplikacje.webp"
            badge={<><Rocket className="h-3.5 w-3.5" /> Największa oszczędność!</>}
            badgeCls="bg-gradient-to-r from-amber-300 via-rose-400 to-fuchsia-400"
            title="Pakiet MEGA — wszystkie aplikacje i gry DigiLib"
            desc="Absolutnie wszystko z oferty edukacyjnej w jednym pakiecie: oba DigiTutory, kursy DigiLingua, DigiKids, DigiMind oraz wszystkie gry DigiLib World i DigiLib Race — także przyszłe premiery. Po zakupie sam wybierasz w koncie, z czego korzystasz."
            items={["DigiTutor AI + Kids", "DigiLingua ×5", "DigiKids ×2", "DigiMind", "DigiLib World ×7", "DigiLib Race ×7"]}
            product={bundleApps}
            tall
          />
        </div>
      )}
    </section>
  );
}
